import React, { useCallback, useEffect, useState } from 'react';
import { MessageInterface } from '../../../../shared/type/message.type';
import { PaginationInterface } from '../../../../shared/type/paginationInterface.type';
import DeleteMessageAlert, {
  defaultDeleteMessagePopUpContext
} from '../DeleteMessageAlert/DeleteMessageAlert';

import { ParentMessageList } from './ParentMessageList';
import { useSafeTranslation } from '../../../../hooks/useSafeTranslation';
import { useLocation } from 'react-router-dom';
import { useParentDashboardContextContext } from "../../../../context/ParentDashboardContext";

interface Props {
  tenantId: string;
  setRefreshMessages: React.Dispatch<React.SetStateAction<() => void>>;
  fetchMessagesFn: (params: {
    tenantId: string;
    pageNumber: number;
    pageSize: number;
  }) => Promise<{ data: MessageInterface[] } & PaginationInterface>;
  deleteMessageFn?: (threadId: number) => Promise<void>;
}

export const ParentMessagePageComponent = ({
  tenantId,
  setRefreshMessages,
  fetchMessagesFn,
  deleteMessageFn
}: Props) => {
  const { fetchParentDashboardContext } = useParentDashboardContextContext();
  const location = useLocation();
  const { t } = useSafeTranslation();
  const [messages, setMessages] = useState<MessageInterface[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInterface>({
    totalCount: 0,
    hasNextPage: false,
    next: { limit: 0, offset: 0 }
  });
  const [deleteMessagePopUp, setDeleteMessagePopUp] = useState(defaultDeleteMessagePopUpContext);

  const fetchMessages = async (page = currentPage) => {
    if (!tenantId || !tenantId.length) return;

    try {
      const res = await fetchMessagesFn({
        tenantId,
        pageNumber: page,
        pageSize: 10
      });

      setMessages(res.data);
      setPagination({
        totalCount: res.totalCount,
        hasNextPage: res.hasNextPage,
        next: res.next
      });
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  useEffect(() => {
    fetchMessages(currentPage);
    fetchParentDashboardContext();
  }, [tenantId, currentPage, location.pathname]);

  useEffect(() => {
    setRefreshMessages(() => () => fetchMessages(currentPage));
  }, [currentPage, setRefreshMessages, tenantId]);

  const goToNextPage = () => pagination.hasNextPage && setCurrentPage((p) => p + 1);
  const goToPreviousPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  const handleDelete = useCallback(async () => {
    try {
      if (!deleteMessageFn) return;
      await deleteMessageFn(deleteMessagePopUp.threadId);
    } catch (e) {
      console.error('Error deleting message:', e);
    } finally {
      fetchMessages(currentPage);
      setDeleteMessagePopUp(defaultDeleteMessagePopUpContext);
    }
  }, [deleteMessagePopUp.threadId, currentPage, deleteMessageFn]);

  return (
    <div className="ParentMessage flex flex-col">
      <div className="pt-spacing-md pr-spacing-4xl pb-spacing-md pl-spacing-4xl">
        <div className="flex justify-end">
          <div className="flex gap-spacing-lg items-center">
            <p className="text-sm font-medium text-grayLight-700">
              {t('parent_mailbox.parent_message.page_info', {
                currentPage: currentPage,
                totalPages: Math.ceil(pagination.totalCount / 10)
              })}
            </p>
            <div className="flex gap-spacing-lg">
              <button onClick={goToPreviousPage} disabled={currentPage === 1}>
                ←
              </button>
              <button onClick={goToNextPage} disabled={!pagination.hasNextPage}>
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      <ParentMessageList
        tenantId={tenantId}
        messages={messages}
        onDelete={(id) => setDeleteMessagePopUp({ isOpen: true, threadId: id })}
        hideDeleteButton={!deleteMessageFn}
      />

      <DeleteMessageAlert
        setDeleteMessagePopUp={setDeleteMessagePopUp}
        isOpen={deleteMessagePopUp.isOpen}
        threadId={deleteMessagePopUp.threadId}
        onFinish={handleDelete}
      />
    </div>
  );
};
